import React, {useRef} from 'react';
import Caret from './Caret.web';
import {useTheme} from '../../../../../utils/theming/themeProvider';
import {PopoverProps} from '../tooltip_types.web';
import {popoverStyles} from '../tooltip_styles.web';

const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  {
    animated = true,
    backgroundColor,
    caret: withCaret = true,
    caretPosition = 'center',
    children,
    textAlign = 'center',
    forceInitialAnimation = false,
    numberOfLines,
    visible = false,
    position = 'bottom',
    style,
    ...extraProps
  },
  ref,
) {
  const isContentString = typeof children === 'string';
  const isHorizontalLayout = position === 'left' || position === 'right';
  const opacityRef = useRef(visible ? 1 : 0);
  const {colors, fontStyles} = useTheme();

  const animationRef = useRef<HTMLDivElement | null>(null);

  const top = position === 'bottom' ? '100%' : undefined;
  const bottom = position === 'top' ? '100%' : undefined;
  const left =
    position === 'right'
      ? '100%'
      : (position === 'top' || position === 'bottom') &&
        caretPosition === 'left'
      ? 0
      : undefined;
  const right =
    position === 'left'
      ? '100%'
      : (position === 'top' || position === 'bottom') &&
        caretPosition === 'right'
      ? 0
      : undefined;

  const transformToHorizontalCenter =
    caretPosition === 'center' && (position === 'top' || position === 'bottom');
  const transformToVerticalCenter =
    caretPosition === 'center' && (position === 'left' || position === 'right');
  const caret = (
    <Caret
      align={caretPosition}
      position={position}
      backgroundColor={backgroundColor}
      style={popoverStyles.caret}
    />
  );

  const positionStyles = {
    center: {},
    right: {marginLeft: 8},
    top: {marginBottom: 8},
    left: {marginRight: 8},
    bottom: {marginTop: 8},
  };

  return (
    <div
      ref={ref}
      style={{
        ...popoverStyles.container,
        ...style,
        pointerEvents: visible ? 'auto' : 'none',
        top: isHorizontalLayout ? '50%' : top,
        bottom: bottom,
        left: transformToHorizontalCenter ? '50%' : left,
        right: right,
        transform: isHorizontalLayout
          ? `translateY(-50%)`
          : transformToHorizontalCenter
          ? `translateX(-50%)`
          : transformToVerticalCenter
          ? `translateY(-50%)`
          : undefined,
        caretPosition,
        ...positionStyles[position],
      }}
      {...extraProps}>
      <div
        ref={animationRef}
        style={{
          opacity: opacityRef.current,
          alignItems:
            position === 'left' ||
            position === 'right' ||
            caretPosition === 'center'
              ? 'center'
              : caretPosition === 'left'
              ? 'flex-start'
              : 'flex-end',
          display: 'flex',
          flexDirection: isHorizontalLayout ? 'row' : 'column',
        }}>
        {withCaret && (position === 'bottom' || position === 'right') && caret}

        <div
          style={{
            ...popoverStyles.content,
            ...(isContentString ? popoverStyles.contentTextOnly : {}),
            backgroundColor: backgroundColor || colors.neutral1000,
            alignSelf: 'stretch',
          }}>
          {isContentString ? (
            <p
              style={{
                color: colors.neutral100,
                textAlign: textAlign,
                margin: 0,
                ...fontStyles['bodyRegularXS'],
                lineHeight: '14.5px',
                display: numberOfLines ? '-webkit-box' : 'block',
                WebkitLineClamp: numberOfLines,
                WebkitBoxOrient: 'vertical',
              }}>
              {children}
            </p>
          ) : (
            children
          )}
        </div>

        {withCaret && (position === 'top' || position === 'left') && caret}
      </div>
    </div>
  );
});

export default Popover;
